// ============================================================
// TERROR ENGINE — THE NPC STAGE  (the keeper on screen with you)
// ------------------------------------------------------------
// caP'n's beat, twice over:
//
//   "V'yen's Sweet Treats as a DIALOGUE WINDOW. Her on screen
//    with you, not a bulletin board. J'ynxi always in the
//    background."
//   "Venus's Booster Shop, same treatment. Her on screen beside
//    the real purchasable packs."
//
// WHY THIS FILE EXISTS RATHER THAN TWO COPIES
//   Onboarding already had the shape — full-height figures anchored
//   to the floor, the speaker lit and the listener dimmed, a bar of
//   prose across the bottom. It was locked inside Onboarding.js and
//   could only ever be Willy and V'ael. Every stall that wanted the
//   same treatment had to grow its own copy, and the third copy is
//   where a house style quietly becomes four different house styles.
//
//   So the stage moved out here, keeper-agnostic. A stall becomes a
//   dialogue window by naming a keeper, not by writing a layout.
//
// ⭐ THE ART CONTRACT — this is the part that matters later.
//   A keeper's picture is a CDN filename and NOTHING ELSE. Neither
//   V'yen nor Venus has been painted yet; both render a lettered
//   plinth in their own colour today, and the DAY their art lands at
//
//        terror-assets/vyen.webp     terror-assets/venus.webp
//
//   they appear on their own stages with ZERO code changes. The
//   <img> onError is the whole fallback mechanism — the same one
//   COMPANION_SPRITES uses, and the same one that let `vael.webp`
//   sit unpainted in Onboarding for a month without anybody
//   noticing a hole. Do not "fix" this by gating on a manifest of
//   what exists; the manifest is what goes stale.
//
// THE THREE SLOTS
//   KEEPER   — the one you came to talk to. Lit, full height, hers
//              is the side the prose is aimed at.
//   YOU      — your own sprite, your own gender, opposite side, a
//              touch smaller and dimmer. This is the entire
//              difference between "a menu" and "her on screen WITH
//              you," and it costs one <img>.
//   LURKER   — the background presence. Small, far, dim, drifting,
//              never addressed. J'ynxi at V'yen's is caP'n's own
//              beat: she is INDIFFERENT, so she does not react, she
//              does not comment, and the player is never told she
//              is there. She simply is, most of the time.
//
// EXPORTS
//   <NpcStage>      full-screen staged window (V'yen)
//   <KeeperMargin>  a lone keeper standing in the margin beside the
//                   canonical stage rectangle (Venus, whose shop is
//                   already a room and must stay one)
//   <StageFigure>   the low-level figure, if a screen wants one
//   KEEPERS         the registry
// ============================================================

import { useState, useEffect, useMemo } from "react";
import { GM, SD } from "./theme";
import { CDN } from "./CDN";
import { useMusicOverride, KEEPER_TRACK } from "./Soundtrack";
import { getIdleSprite } from "./SpriteManifest";
import KeeperPortrait3D from "./keeperPortrait3D";
import { folkIdFor } from "./plazaFolk3D"; // the being's ONE body, on the stage (caP'n, 2026-09-23)

// ── THE KEEPER REGISTRY ───────────────────────────────────────
// `file` is a filename, not a URL, and not a promise that it
// exists. `letter` is what stands there until it does.
export const KEEPERS = {
  vyen: {
    name:    "V'yen",
    kicker:  "SWEET TREATS",
    file:    "vyen.webp",
    model:   "vyen",        // the 3D body (plazaFolk3D) — wins over the picture
    accent:  SD.red,
    letter:  "V",
    // The serpent coil is her whole silhouette in the Plaza art, so
    // the plinth leans on a coil rather than a slab.
    shape:   "coil",
  },
  venus: {
    // caP'n, 2026-08-18: "Tiny's" was a default that stuck. She is
    // VENUS, and she has been at that counter since before undeath.
    name:    "Venus",
    kicker:  "CARD PACKS",
    file:    "venus.webp",
    model:   "venus",        // the 3D body (plazaFolk3D) — wins over the picture
    accent:  GM.inkGold,
    letter:  "V",
    shape:   "slab",
  },
  vael: {
    name:    "V'ael",
    kicker:  "",
    file:    "vael.webp",
    model:   "vael",        // the 3D body (plazaFolk3D) — wins over the picture
    accent:  "#d44a2a",
    letter:  "V",
    shape:   "ember",
  },
  willy: {
    name:    "Willy",
    kicker:  "",
    file:    "willy.webp",
    model:   "willy",        // the 3D body (plazaFolk3D) — wins over the picture
    accent:  GM.inkGold,
    letter:  "W",
    shape:   "slab",
  },
};

export const keeperUrl = (id) =>
  KEEPERS[id]?.file ? `${CDN}/${KEEPERS[id].file}` : null;

// ── KEYFRAMES ─────────────────────────────────────────────────
// Imperative into document.head with an id-guard AND a textContent
// compare. Never a JSX <style> block — that is what froze the
// J'ynxi foil, and the one in this very file would be a lurker
// that stops drifting.
const STAGE_CSS = `
@keyframes npcstage-in    { from { opacity:0 } to { opacity:1 } }
@keyframes npcstage-rise  { from { opacity:0; transform:translateY(18px) } to { opacity:1; transform:translateY(0) } }
@keyframes npcstage-drift { 0%{ transform:translateY(0) } 50%{ transform:translateY(-7px) } 100%{ transform:translateY(0) } }
@keyframes npcstage-fade  { 0%{ opacity:0 } 12%{ opacity:.34 } 88%{ opacity:.34 } 100%{ opacity:0 } }
`;
function ensureStageKeyframes() {
  if (typeof document === "undefined") return;
  const ID = "terror-npc-stage-css";
  let el = document.getElementById(ID);
  if (el && el.textContent === STAGE_CSS) return;
  if (!el) {
    el = document.createElement("style");
    el.id = ID;
    document.head.appendChild(el);
  }
  el.textContent = STAGE_CSS;
}

// ── THE PLINTH — what stands there until the art lands ────────
// Deliberately NOT a grey box. It is lit in the keeper's own
// colour with her own initial, so an unpainted stage still reads
// as "somebody is standing here" rather than as a loading failure.
function Plinth({ keeper }) {
  const a = keeper.accent;
  const coil = keeper.shape === "coil";
  const ember = keeper.shape === "ember";
  return (
    <div style={{
      width: "62%", height: "68%",
      display: "flex", alignItems: "center", justifyContent: "center",
      borderRadius: coil ? "48% 48% 14% 14% / 60% 60% 10% 10%" : 12,
      border: `2px solid ${a}88`,
      background: ember
        ? `radial-gradient(ellipse at 50% 62%, ${a}55 0%, transparent 74%)`
        : `linear-gradient(160deg, ${a}22, rgba(0,0,0,0) 72%)`,
      boxShadow: `0 0 42px ${a}44, inset 0 0 40px rgba(0,0,0,0.62)`,
    }}>
      <div style={{
        fontSize: "clamp(90px, 17vh, 210px)", fontWeight: 900, fontStyle: "italic",
        color: a, fontFamily: GM.font, lineHeight: 1,
        textShadow: `0 0 38px ${a}, 0 0 10px ${a}`,
      }}>{keeper.letter}</div>
    </div>
  );
}

// ── STAGE FIGURE ──────────────────────────────────────────────
// One body on the stage. `src` wins if it loads; the plinth is what
// is left when it does not. Everything about brightness is driven
// by `active` so the eye always knows who is talking.
export function StageFigure({
  keeperId,
  src        = null,      // explicit url (the player's own sprite)
  keeper     = null,      // explicit keeper object (overrides registry)
  side       = "right",
  active     = true,
  width      = "44vw",
  height     = "88vh",
  offset     = 0,         // px inset from the edge
  pixelated  = false,
  glow       = true,
  fallback   = null,
  model      = null,      // a folk body id to draw in 3D (the player's own body)
  facing     = { x: 0, y: 1 },   // which way the 3D body turns (x>0 = toward screen right)
  acting     = null,      // passed to the 3D body's ctl (expr / talk / act / hands / book / hold)
}) {
  const [failed, setFailed] = useState(false);
  const k = keeper || KEEPERS[keeperId] || null;
  const url = src || (keeperId ? keeperUrl(keeperId) : null);
  const accent = k?.accent || GM.inkGold;

  useEffect(() => { setFailed(false); }, [url]);

  return (
    <div aria-hidden="true" style={{
      position: "absolute", bottom: 0, [side]: offset,
      width, height, maxHeight: height,
      display: "flex", alignItems: "flex-end", justifyContent: "center",
      pointerEvents: "none",
      transition: "opacity .6s ease, filter .6s ease, transform .6s ease",
      opacity: active ? 1 : 0.3,
      filter: active ? "none" : "grayscale(.6) brightness(.55)",
      transform: active ? "translateY(0)" : "translateY(8px)",
      zIndex: 1,
    }}>
      {/* ⭐ THE BODY WINS (caP'n, 2026-09-23: "the character model is
          here … get rid of this placeholder"). A keeper with a 3D body
          stands on her stage in 3D; FolkBody carries its own painted
          fail-safe, so the picture/plinth path below is only for keepers
          with no body yet — and for the player's own sprite (`src`). */}
      {(model || (!src && k?.model)) ? (
        <div style={{ width: "100%", height: "100%",
          filter: active && glow ? `drop-shadow(0 -10px 40px ${accent}55)` : "none" }}>
          <KeeperPortrait3D id={model || k.model} facing={facing} acting={acting} />
        </div>
      ) : url && !failed ? (
        <img
          src={url}
          alt=""
          onError={() => setFailed(true)}
          style={{
            maxWidth: "100%", maxHeight: "100%",
            objectFit: "contain", objectPosition: "center bottom",
            display: "block",
            imageRendering: pixelated ? "pixelated" : "auto",
            filter: active && glow ? `drop-shadow(0 -10px 40px ${accent}55)` : "none",
          }}
        />
      ) : (
        fallback !== null ? fallback : (k ? <Plinth keeper={k} /> : null)
      )}
    </div>
  );
}

// ── THE LURKER ────────────────────────────────────────────────
// J'ynxi in the background. Three rules, and they are the beat:
//   1. She is SMALL and FAR — a third of the keeper's height, up
//      the stage, behind everything.
//   2. She DRIFTS, she does not animate. Nothing about her asks
//      for the eye.
//   3. "Frequently" is not "always." She fades in and out on her
//      own long cycle, so a player who looks twice sees a
//      different room and is never told why.
// She is INDIFFERENT (canon): she never reacts to what the keeper
// says, and nothing here is allowed to give her a line.
function Lurker({ id = "jynxi", x = "58%", presence = 0.34, always = false }) {
  return (
    <div aria-hidden="true" style={{
      position: "absolute", bottom: "16%", left: x,
      width: "clamp(90px, 15vh, 190px)", height: "clamp(90px, 15vh, 190px)",
      pointerEvents: "none", zIndex: 0,
      opacity: always ? presence : undefined,
      animation: always
        ? "npcstage-drift 7s ease-in-out infinite"
        : "npcstage-fade 26s ease-in-out infinite, npcstage-drift 7s ease-in-out infinite",
      filter: "grayscale(.35) brightness(.7) blur(0.4px)",
    }}>
      <img
        src={`${CDN}/${id}.webp`}
        alt=""
        onError={e => { e.target.style.display = "none"; }}
        style={{
          width: "100%", height: "100%", objectFit: "contain",
          objectPosition: "center bottom", imageRendering: "pixelated",
          display: "block",
        }}
      />
    </div>
  );
}

// ── PLAYER SPRITE RESOLVER ────────────────────────────────────
// The player's OWN body, their own gender, from the same manifest
// the Plaza walks them around with. A stage that draws a generic
// silhouette here is a stage the player is not standing on.
export function usePlayerStageSprite(profile) {
  return useMemo(
    () => getIdleSprite(profile?.gender === "f" ? "f" : "m", false),
    [profile?.gender]
  );
}

// ══════════════════════════════════════════════════════════════
// <NpcStage> — the full staged window
// ══════════════════════════════════════════════════════════════
// PROPS
//   keeperId    "vyen" | "venus" | ...        (required)
//   line        string  — what she is saying right now
//   profile     the player's profile (gender → their sprite)
//   lurker      "jynxi" | null | false        (default: none)
//   lurkerAlways  bool — hold her at a steady presence instead of
//                        letting her fade in and out
//   backdrop    CDN filename for the scene behind everything
//   badge       tiny kicker over her name (defaults to her own)
//   onClose     fn — the ✕ / Escape route
//   footer      node rendered under the well (back buttons etc)
//   children    the actual UI — matchmaking, packs, whatever
//   wide        bool — a wider content well for shelf-shaped UI
//
// GEOMETRY, and why it is this way: the figures are anchored to the
// BOTTOM and the content well floats in the MIDDLE. Anchoring the
// well to the bottom (the visual-novel default) works when the box
// holds three lines of prose and fails the moment it holds a deck
// picker, because the box then grows up over the faces it was
// supposed to sit under. Middle-anchored, the well grows in both
// directions and the heads stay clear.
export default function NpcStage({
  keeperId,
  line       = "",
  profile    = null,
  lurker     = null,
  lurkerAlways = false,
  backdrop   = "plaza_1.webp",
  badge      = null,
  onClose,
  footer     = null,
  children,
  wide       = false,
  zIndex     = 200,
  acting     = null,      // the keeper's acting on the stage, e.g. { expr: "smile", talk: true, act: "wave" } (a being that has acts: Willie, 2026-09-25)
}) {
  const keeper = KEEPERS[keeperId] || KEEPERS.vyen;
  const accent = keeper.accent;
  // ♪ While this keeper's window is open, her song owns the music (V'yen and
  // V'ael = Sweet Treats). No row in KEEPER_TRACK = the hub keeps playing.
  useMusicOverride(KEEPER_TRACK[keeperId] || null);
  const playerSprite = usePlayerStageSprite(profile);
  const [narrow, setNarrow] = useState(
    typeof window !== "undefined" && window.innerWidth < 720
  );

  useEffect(() => { ensureStageKeyframes(); }, []);

  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < 720);
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  // Escape leaves. The kiosk this replaced already taught BACKSPACE
  // = back and owns that key itself; this only claims Escape so the
  // two never fight over the same press.
  useEffect(() => {
    if (!onClose) return;
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onClose(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${keeper.name} — ${badge || keeper.kicker || "conversation"}`}
      style={{
      position: "fixed", inset: 0, zIndex,
      background: "rgba(3,2,8,0.90)",
      overflow: "hidden",
      fontFamily: GM.font,
      animation: "npcstage-in .35s ease",
    }}>
      {/* ── THE SCENE ── the Plaza itself, pushed back behind glass.
          It is the room she is actually standing in, so the window
          reads as a place rather than as a modal that happens to
          have a picture in it. */}
      {backdrop && (
        <img
          src={`${CDN}/${backdrop}`}
          alt=""
          aria-hidden="true"
          onError={e => { e.target.style.display = "none"; }}
          style={{
            position: "absolute", inset: 0, width: "100%", height: "100%",
            objectFit: "cover", opacity: 0.30,
            filter: "blur(3px) saturate(.72) brightness(.55)",
            pointerEvents: "none",
          }}
        />
      )}
      <div aria-hidden="true" style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        background: `
          radial-gradient(ellipse at 78% 72%, ${accent}1f, transparent 58%),
          linear-gradient(to top, rgba(3,2,8,0.94) 0%, rgba(3,2,8,0.28) 46%, rgba(3,2,8,0.72) 100%)
        `,
      }} />

      {/* ── THE LURKER ── behind both figures, never addressed ── */}
      {lurker && <Lurker id={lurker === true ? "jynxi" : lurker} always={lurkerAlways} />}

      {/* ── YOU ── your own body, your own gender, dimmer than her.
          Hidden on narrow screens: two figures plus a content well
          across 400px is three things fighting for one column, and
          the one that has to win is the well. */}
      {/* ⭐ THE SIDES ARE LAW (caP'n, 2026-09-23): "to keep continuity,
          every single npc that pops up on the screen will be on the
          LEFT, the player's character will be on the RIGHT." And the
          player is their 3D body now, not the sprite. */}
      {!narrow && (
        <StageFigure
          src={playerSprite}
          model={folkIdFor(profile?.gender === "f" ? "f" : "m")}
          facing={{ x: -0.45, y: 1 }}
          side="right"
          offset="1vw"
          width="28vw"
          height="72vh"
          active={false}
          glow={false}
          fallback={null}
        />
      )}

      {/* ── HER ── */}
      <StageFigure
        keeperId={keeperId}
        acting={acting}
        facing={{ x: 0.35, y: 1 }}
        side="left"
        offset={narrow ? "-6vw" : "0.5vw"}
        width={narrow ? "62vw" : "40vw"}
        height={narrow ? "62vh" : "90vh"}
        active
      />

      {/* ── THE WELL ── nameplate, her line, and the room's own UI ── */}
      <div style={{
        position: "absolute",
        left: narrow ? "50%" : (wide ? "50%" : "54%"),
        top: "50%",
        transform: "translate(-50%,-50%)",
        width: wide ? "min(94vw, 880px)" : "min(92vw, 560px)",
        maxHeight: "88vh", overflowY: "auto", overflowX: "hidden",
        zIndex: 4,
        animation: "npcstage-rise .45s ease-out",
      }}>
        {/* Nameplate — floats over the well on her side, exactly the
            idiom Onboarding uses, so the two screens read as one
            game rather than as two conventions. */}
        <div style={{ textAlign: "center", marginBottom: 14 }}>
          <div style={{
            display: "inline-block",
            background: `linear-gradient(135deg, ${accent}33, rgba(8,6,14,0.94))`,
            border: `1px solid ${accent}`, borderRadius: 3,
            padding: "4px 16px", fontSize: 10, letterSpacing: 5,
            fontWeight: 900, color: accent,
            textShadow: `0 0 8px ${accent}`,
            boxShadow: `0 0 14px ${accent}55`,
          }}>
            ◆ {(badge || keeper.kicker || keeper.name).toUpperCase()} ◆
          </div>
          <div style={{
            fontSize: 26, fontWeight: 900, color: GM.inkGoldBright,
            fontFamily: GM.font, marginTop: 8, letterSpacing: 1,
          }}>{keeper.name}</div>
          {line && (
            <div style={{
              fontSize: 12, color: GM.vellumFaint, fontStyle: "italic",
              lineHeight: 1.7, marginTop: 6, padding: "0 6px",
            }}>{line}</div>
          )}
        </div>

        <div style={{
          background: `linear-gradient(150deg, rgba(10,7,18,0.95), rgba(4,3,10,0.97))`,
          border: `1px solid ${accent}66`,
          borderRadius: 10,
          padding: "18px 20px 16px",
          boxShadow: `0 20px 60px rgba(0,0,0,0.75), 0 0 26px ${accent}22`,
        }}>
          {children}
        </div>

        {footer}
      </div>

      {/* ── LEAVE ── */}
      {onClose && (
        <button
          onClick={onClose}
          aria-label="Leave"
          style={{
            position: "absolute", top: 14, right: 16, zIndex: 6,
            width: 34, height: 34, borderRadius: 17, cursor: "pointer",
            background: "rgba(8,6,14,0.85)", border: `1px solid ${GM.pageBorder}`,
            color: GM.vellumFaint, fontSize: 15, lineHeight: 1,
            fontFamily: GM.font,
          }}>✕</button>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// <KeeperMargin> — a keeper beside a room, not instead of one
// ══════════════════════════════════════════════════════════════
// Venus's shop is already a ROOM on the canonical stage rectangle
// (C5, The Treatment) and it must stay one — the Emporium's shelves,
// tabs and preview modal are all sized to that rect and rebuilding
// them inside a dialogue well would be a regression dressed as a
// feature. So Venus does not replace her shop; she STANDS BESIDE
// IT, in the margin the letterboxing already leaves empty.
//
// She appears only when there is genuinely room for her: the stage
// rect is square-ish, so on a wide desktop there are a couple of
// hundred spare pixels either side and on a phone there are none.
// Below the threshold she hides completely rather than overlapping
// the shelves — a keeper who covers the thing she is selling is
// worse than no keeper.
//
// PROPS
//   keeperId  registry key
//   line      her current line, shown on a plate at her feet
//   side      "left" | "right"   (default left — the Emporium's own
//             chrome rail lives on the right)
//   minMargin px of free margin required before she appears
// ── THE ROOM CHECK ────────────────────────────────────────────
// Exported on purpose. A caller needs to know whether the keeper
// actually appeared, because the fallback when she does not is NOT
// "nothing" — it is her line, inline, in the room's own header. A
// phone player should still be served by Venus; she just cannot
// stand up at 390px wide.
export function useMarginRoom(stage, minMargin = 150) {
  const [box, setBox] = useState(() => measure(stage));
  useEffect(() => {
    const update = () => setBox(measure(stage));
    update();
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
    // stage.w is the only part that matters; depending on the object
    // identity would re-subscribe on every parent render.
  }, [stage?.w]); // eslint-disable-line react-hooks/exhaustive-deps
  const margin = box ? box.margin : 0;
  return { margin, roomy: margin >= minMargin };
}

export function KeeperMargin({
  keeperId,
  line     = "",
  side     = "left",
  minMargin = 150,
  stage    = null,
  zIndex   = 6,
}) {
  const keeper = KEEPERS[keeperId] || null;
  const { margin, roomy } = useMarginRoom(stage, minMargin);

  useEffect(() => { ensureStageKeyframes(); }, []);

  if (!keeper || !roomy) return null;

  // ⚠ CAPPED, and this is not a style preference. The canonical stage
  // rect is height-limited, so on an ultrawide the margin either side
  // can be 600px+ — and an uncapped keeper then renders as a figure
  // taller and wider than the shop she is standing next to, which
  // reads as "the art broke" rather than as a shopkeeper. 300px is
  // about a person's worth of screen beside a room. She centres in
  // whatever margin is actually there.
  const w = Math.min(margin, 300);
  const pad = Math.round((margin - w) / 2);

  return (
    <div style={{
      position: "fixed", bottom: 0, [side]: pad,
      width: w, height: "82vh",
      pointerEvents: "none", zIndex,
      animation: "npcstage-in .5s ease",
    }}>
      <StageFigure
        keeperId={keeperId}
        side={side}
        offset={0}
        width="100%"
        height="76vh"
        active
      />
      {line && (
        <div style={{
          position: "absolute", bottom: 18, left: 10, right: 10,
          background: "rgba(6,4,12,0.88)",
          border: `1px solid ${keeper.accent}55`,
          borderRadius: 8, padding: "9px 11px",
          fontFamily: GM.font, fontSize: 11, lineHeight: 1.6,
          fontStyle: "italic", color: GM.vellumFaint,
          boxShadow: `0 0 18px ${keeper.accent}22`,
        }}>
          <div style={{
            fontSize: 8, letterSpacing: 3, fontWeight: 900,
            color: keeper.accent, marginBottom: 5,
          }}>{keeper.name.toUpperCase()}</div>
          {line}
        </div>
      )}
    </div>
  );
}

// The margin either side of the canonical stage rectangle. Measured
// off clientWidth, never innerWidth — innerWidth counts the
// scrollbar and hides the band's edge underneath it (the July 2026
// cut-off bug, and it is the same arithmetic here).
function measure(stage) {
  if (typeof document === "undefined") return null;
  const vw = document.documentElement.clientWidth;
  const w = stage?.w;
  if (!w) return null;
  return { margin: Math.max(0, Math.round((vw - w) / 2)) };
}
